import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Routes, Route, NavLink, useParams, Link, Navigate, useNavigate, useLocation, matchPath } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useTheme, THEMES, THEME_CONFIG } from '../contexts/ThemeContext';
import { useDashboardLayout } from '../contexts/DashboardLayoutContext';
import { useTutorial } from '../contexts/TutorialContext';
import { TUTORIAL_TRACK_LABELS, SPEAKER_BUREAU_GUIDE_MARKER } from '../constants/tutorialConstants';
import { updateTutorialState } from '../services/tutorialService';
import { useParadigm } from '../contexts/ParadigmContext';
import { Menu, X, ChevronDown, Building2, Palette, Search, Clock } from 'lucide-react';
import AppSidebarNav from './navigation/AppSidebarNav';
import {
  Archive as NavArchive, BarChart3 as NavBarChart3, Bot as NavBot, Briefcase as NavBriefcase, Building as NavBuilding, Car as NavCar, CheckSquare as NavCheckSquare, ClipboardList as NavClipboardList, Clock as NavClock, CreditCard as NavCreditCard, FileText as NavFileText, GraduationCap as NavGraduationCap, Hammer as NavHammer, Home as NavHome, Inbox as NavInbox, KeyRound as NavKeyRound, Link2 as NavLink2, Mail as NavMail, Map as NavMap, Megaphone as NavMegaphone, Package as NavPackage, Plane as NavPlane, PlaneTakeoff as NavPlaneTakeoff, RefreshCw as NavRefreshCw, Rocket as NavRocket, ScrollText as NavScrollText, Search as NavSearch, Settings as NavSettings, ShieldCheck as NavShieldCheck, Stethoscope as NavStethoscope, Target as NavTarget, Users as NavUsers, Wallet as NavWallet, Wrench as NavWrench, Zap as NavZap,
} from 'lucide-react';

import { getApiBase, getAuthHeaders } from '../utils/api';

// Import dashboard components
import AdminDashboard from './dashboard/AdminDashboard';
import OrganizationManagement from './dashboard/OrganizationManagement';
import OrganizationAdminDashboard from './dashboard/OrganizationAdminDashboard';
import AccessManagement from './admin/AccessManagement';
import TodoManagement from './dashboard/TodoManagement';
import DocumentUpload from './dashboard/DocumentUpload';
import BookingSearch from './dashboard/BookingSearch';
import BookingManagement from './dashboard/BookingManagement';
import Profile from './dashboard/Profile';
import ExecutiveManagement from './dashboard/ExecutiveManagement';
import SpeakerManagement from './dashboard/SpeakerManagement';
import ContactList from './dashboard/ContactList';
import TripManagement from './dashboard/TripManagement';
import CreateTrip from './dashboard/CreateTrip';
import TripDetails from './dashboard/TripDetails';
import TripDetails2 from './dashboard/TripDetails2';
import UnfiledReservations from './dashboard/UnfiledReservations';
import ChauffeuredTransferBooking from './dashboard/ChauffeuredTransferBooking';
import Logs from './dashboard/Logs';
import ParsingLogs from './dashboard/ParsingLogs';
import GooglePlacesLogs from './dashboard/GooglePlacesLogs';
import FlightAwareLogs from './dashboard/FlightAwareLogs';
import FlightAwareReconciliation from './dashboard/FlightAwareReconciliation';
import DuffelLogs from './dashboard/DuffelLogs';
import XeroLogs from './dashboard/XeroLogs';
import ExecDashboardV2 from './dashboard/ExecDashboardV2';
import EmailSettings from './dashboard/EmailSettings';
import PackagesManagement from './dashboard/PackagesManagement';
import LandingPage from './dashboard/LandingPage';
import LandingPageV2 from './dashboard/LandingPageV2';
import LandingPageV3 from './dashboard/LandingPageV3';
import LandingPagePublish from './dashboard/LandingPagePublish';
import EarlyAccessRequests from './dashboard/EarlyAccessRequests';
import SuperadminDashboard from './dashboard/SuperadminDashboard';
import SuperadminSettings from './dashboard/SuperadminSettings';
import EmailVisibilityDiagnostics from './dashboard/EmailVisibilityDiagnostics';
import ParsingQASettings from './dashboard/ParsingQASettings';
import ParsingReviewQueue from './dashboard/ParsingReviewQueue';
import ParsingReviewWorkstation from './parsing-review/ParsingReviewWorkstation';
import DetachedPanelPage from './parsing-review/DetachedPanelPage';
import ParsingQueueOverview from './parsing-review/ParsingQueueOverview';
import AwardWalletSettings from './dashboard/AwardWalletSettings';
import AwardWalletComparisons from './dashboard/AwardWalletComparisons';
import EmailParsingSettings from './dashboard/EmailParsingSettings';
import AdminPermissionsManagement from './dashboard/AdminPermissionsManagement';
import AuditLogs from './dashboard/AuditLogs';
import RBACManagement from './dashboard/RBACManagement';
import EnterpriseSSOConfiguration from './dashboard/EnterpriseSSOConfiguration';
import BillingIntegrations from './admin/BillingIntegrations';
import BillingDashboard from './billing/BillingDashboard';
import AdminBillingDashboard from './billing/AdminBillingDashboard';
import LegalDocumentsManagement from './admin/LegalDocumentsManagement';
import SchedulerDashboard from './admin/SchedulerDashboard';
import SchedulerHistory from './admin/SchedulerHistory';
import AdminTravelTransactions from './admin/AdminTravelTransactions';
import EntitlementsPage from './entitlements/EntitlementsPage';
import TutorialLauncher from './tutorial/TutorialLauncher';
import GettingStartedGuide from './onboarding/GettingStartedGuide';

// Wrapper component to extract tripId from URL params - now using TripDetails2 (new compact design)
const TripDetailsWrapper = () => {
  const { tripId } = useParams();
  return <TripDetails2 tripId={tripId} />;
};

// Wrapper component for legacy TripDetails (kept at /TripLegacy for rollback if needed)
const TripDetailsLegacyWrapper = () => {
  const { tripId } = useParams();
  return <TripDetails tripId={tripId} />;
};

// Wrapper component for admin entitlements view
const AdminEntitlementsWrapper = ({ currentUser }) => {
  const { orgId } = useParams();
  return <EntitlementsPage organizationId={orgId} currentUser={currentUser} isAdmin={true} />;
};

// Screens built edge to edge (they lay out their own gutters); every other page
// gets the standard page padding in both themes.
const FULL_BLEED_ROUTES = ['/', '/Trip/:tripId', '/diagnostics/flightaware-reconciliation'];

const Dashboard = () => {
  const location = useLocation();
  const fullBleed = FULL_BLEED_ROUTES.some(path => matchPath(path, location.pathname));
  const { sidebarOpen, setSidebarOpen, expandedMenus, setExpandedMenus, closeSidebar } = useDashboardLayout();
  const { startTutorial, tutorialEnabledForOrg, getAvailableTracks, state: tutorialState } = useTutorial();
  const [orgDropdownOpen, setOrgDropdownOpen] = useState(false);
  const [orgSearchTerm, setOrgSearchTerm] = useState('');
  const [themeDropdownOpen, setThemeDropdownOpen] = useState(false);
  const [rbacStatus, setRbacStatus] = useState(null);
  const [ssoFeaturesAuthorized, setSsoFeaturesAuthorized] = useState({ saml: false, oidc: false });
  const [bookingEnabled, setBookingEnabled] = useState(false);
  const [recentOrgIds, setRecentOrgIds] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('recentOrganizations') || '[]');
    } catch {
      return [];
    }
  });
  const { user, logout, actingOrganization, organizations, hasMultipleOrgs, fetchOrganizations, switchOrganization, setPrimaryOrganization } = useAuth();
  const { theme, setTheme, themeConfig } = useTheme();
  const { labels, getRoleLabel, isSpeakerBureau } = useParadigm();
  const orgSearchInputRef = useRef(null);
  const orgDropdownRef = useRef(null);

  const isSuperadmin = user?.role === 'superadmin';
  const isExecutiveUser = user?.role === 'executive';
  const isSpeakerUser = user?.role === 'speaker';
  // Users limited to their own dashboard + trips (no planning/admin surfaces)
  const isRestrictedUser = isExecutiveUser || isSpeakerUser;
  const adminPermissions = user?.admin_permissions || [];
  const hasAnyAdminPermission = adminPermissions.length > 0;

  // Helper to check if user has a specific admin permission
  const hasPermission = (permission) => adminPermissions.includes(permission);

  // Helper to check if user has a specific RBAC permission
  const hasRbacPermission = (permission) => {
    if (!rbacStatus?.rbac_enabled) return false;
    const userPerms = rbacStatus?.user_permissions || [];
    // org_admin grants all permissions
    if (userPerms.includes('org_admin')) return true;
    return userPerms.includes(permission);
  };

  // Determine effective org admin status - respects RBAC when enabled
  // When RBAC is enabled: user must have org_admin RBAC permission
  // When RBAC is not enabled: use legacy is_organization_admin flag
  const isEffectiveOrgAdmin = rbacStatus?.rbac_enabled
    ? rbacStatus?.user_permissions?.includes('org_admin')
    : user?.is_organization_admin;

  // First-login onboarding for speaker bureau org admins: open the standalone
  // Getting Started guide once (replaces the old overlay walkthrough). A marker
  // is persisted in tutorial_state so it never auto-opens again. Superadmins
  // are excluded - they reach the guide from the sidebar when reviewing.
  const navigate = useNavigate();
  const guideAutoOpenedRef = useRef(false);
  useEffect(() => {
    if (guideAutoOpenedRef.current) return;
    if (!user || !isSpeakerBureau || isSuperadmin || isRestrictedUser) return;
    if (!isEffectiveOrgAdmin || !tutorialEnabledForOrg) return;
    const ts = user.ui_preferences?.tutorial_state || {};
    if (ts.auto_start_disabled) return;
    const completedTracks = ts.completed_tracks || [];
    if (completedTracks.includes(SPEAKER_BUREAU_GUIDE_MARKER)) return;

    guideAutoOpenedRef.current = true;
    updateTutorialState({ completed_tracks: [...completedTracks, SPEAKER_BUREAU_GUIDE_MARKER] })
      .catch(() => { /* non-fatal: guide simply auto-opens again next login */ });
    navigate('/getting-started');
  }, [user, isSpeakerBureau, isSuperadmin, isRestrictedUser, isEffectiveOrgAdmin, tutorialEnabledForOrg, navigate]);

  // Check if user has any RBAC permissions (but not org admin)
  // This is for showing the "Organization Admin" submenu to users with specific RBAC permissions
  // Uses isEffectiveOrgAdmin to properly exclude org admins (they get the full Admin menu instead)
  const hasAnyRbacPermission = rbacStatus?.rbac_enabled &&
    (rbacStatus?.user_permissions?.length > 0) &&
    !isEffectiveOrgAdmin;

  // Build Site Admin submenu based on user's permissions
  const buildSiteAdminSubmenu = () => {
    const submenu = [];

    // App Administration - requires app_administration permission
    if (hasPermission('app_administration')) {
      submenu.push({ name: 'App Administration', icon: NavWrench, isNested: true, submenu: [
        { name: 'Users / Permissions', href: '/admin-permissions', icon: NavShieldCheck },
      ]});
    }

    // Organizations & Packages
    const orgPackagesItems = [];
    if (hasPermission('organizations')) {
      orgPackagesItems.push({ name: 'Organizations', href: '/organizations', icon: NavBuilding });
    }
    if (hasPermission('packages')) {
      orgPackagesItems.push({ name: 'Packages', href: '/packages', icon: NavPackage });
    }
    if (orgPackagesItems.length > 0) {
      submenu.push({ name: 'Organizations & Packages', icon: NavBuilding, isNested: true, submenu: orgPackagesItems });
    }

    // Content & Marketing
    const contentItems = [];
    if (hasPermission('landing_pages')) {
      contentItems.push({ name: 'Landing Page', href: '/landing', icon: NavRocket });
      contentItems.push({ name: 'Landing Page (v2)', href: '/landing-v2', icon: NavRocket });
      contentItems.push({ name: 'Landing Page (v3)', href: '/landing-v3', icon: NavRocket });
      contentItems.push({ name: 'Publish Landing Page', href: '/landing-publish', icon: NavMegaphone });
    }
    if (hasPermission('early_access')) {
      contentItems.push({ name: 'Early Access Requests', href: '/early-access-requests', icon: NavFileText });
    }
    if (hasPermission('legal_documents')) {
      contentItems.push({ name: 'Legal Documents', href: '/legal-documents', icon: NavScrollText });
    }
    if (contentItems.length > 0) {
      submenu.push({ name: 'Content & Marketing', icon: NavRocket, isNested: true, submenu: contentItems });
    }

    // User Management
    const userMgmtItems = [];
    if (hasPermission('users_invites')) {
      userMgmtItems.push({ name: 'Users & Invites', href: '/admin', icon: NavUsers });
    }
    if (hasPermission('teams_permissions')) {
      userMgmtItems.push({ name: 'Teams & Permissions', href: '/access-management', icon: NavKeyRound });
      userMgmtItems.push({ name: 'Email Directory', href: '/access-management?tab=email-directory', icon: NavMail });
    }
    if (userMgmtItems.length > 0) {
      submenu.push({ name: 'User Management', icon: NavUsers, isNested: true, submenu: userMgmtItems });
    }

    // Billing & Finance
    const billingItems = [];
    if (hasPermission('admin_billing')) {
      billingItems.push({ name: 'Admin Billing', href: '/admin-billing', icon: NavWallet });
      billingItems.push({ name: 'Travel Transactions', href: '/admin-travel', icon: NavPlane });
    }
    if (hasPermission('billing_integrations')) {
      billingItems.push({ name: 'Billing & Integrations', href: '/integrations', icon: NavCreditCard });
    }
    if (hasPermission('billing_scheduler')) {
      billingItems.push({ name: 'Billing Scheduler', href: '/admin/scheduler', icon: NavClock });
    }
    if (billingItems.length > 0) {
      submenu.push({ name: 'Billing & Finance', icon: NavWallet, isNested: true, submenu: billingItems });
    }

    // Superadmin Dashboard (direct link for users with parsing_feedback permission)
    if (hasPermission('parsing_feedback')) {
      submenu.push({ name: 'Superadmin Dashboard', href: '/superadmin', icon: NavBarChart3 });
    }

    // Parsing & Data
    if (hasPermission('parsing_feedback')) {
      submenu.push({ name: 'Parsing & Data', icon: NavTarget, isNested: true, submenu: [
        { name: 'Review Queue', href: '/parsing-review', icon: NavClipboardList },
        { name: 'AI & Parsing Settings', href: '/superadmin-settings', icon: NavBot },
      ]});
    }

    // Logs
    if (hasPermission('logs')) {
      submenu.push({ name: 'Logs', icon: NavClipboardList, isNested: true, submenu: [
        { name: 'FlightAware Logs', href: '/flightaware-logs', icon: NavPlane },
        { name: 'Duffel API Logs', href: '/duffel-logs', icon: NavPlaneTakeoff },
          { name: 'Xero API Logs', href: '/xero-logs', icon: NavBarChart3 },
        { name: 'API Logs', href: '/logs', icon: NavFileText },
        { name: 'Google Maps Logs', href: '/google-places-logs', icon: NavMap },
        { name: 'Parsing Log', href: '/parsing-logs', icon: NavBot },
        { name: 'Audit Logs', href: '/audit-logs', icon: NavBarChart3 },
      ]});
    }

    return submenu;
  };

  // Build RBAC Admin submenu based on user's RBAC permissions
  const buildRbacAdminSubmenu = () => {
    const submenu = [];

    // Users & Invites - requires users_invites permission
    if (hasRbacPermission('users_invites')) {
      submenu.push({ name: 'Users & Invites', href: '/admin', icon: NavUsers });
    }
    // Teams - requires teams permission
    if (hasRbacPermission('teams')) {
      submenu.push({ name: 'Teams', href: '/access-management?tab=teams', icon: NavUsers });
    }
    // User Assignments - requires user_assignments permission
    if (hasRbacPermission('user_assignments')) {
      submenu.push({ name: 'User Assignments', href: '/access-management?tab=assignments', icon: NavLink2 });
    }
    // Manager Relationships - requires manager_relationships permission
    if (hasRbacPermission('manager_relationships')) {
      submenu.push({ name: 'Manager Relationships', href: '/access-management?tab=managers', icon: NavBriefcase });
    }
    // Email Directory - available to admins
    submenu.push({ name: 'Email Directory', href: '/access-management?tab=email-directory', icon: NavMail });
    // Audit Logs - requires audit_logs permission
    if (hasRbacPermission('audit_logs') || hasRbacPermission('audit_logs_configure')) {
      submenu.push({ name: 'Audit Logs', href: '/audit-logs', icon: NavClipboardList });
    }
    // Enterprise SSO Configuration - requires saml_configure permission AND at least one SSO feature authorized
    const hasSsoFeature = ssoFeaturesAuthorized.saml || ssoFeaturesAuthorized.oidc;
    if (hasRbacPermission('saml_configure') && hasSsoFeature) {
      submenu.push({ name: 'Enterprise SSO', href: '/sso-config', icon: NavKeyRound });
    }
    // Billing - requires billing_view permission
    if (hasRbacPermission('billing_view')) {
      submenu.push({ name: 'Billing', href: '/billing', icon: NavCreditCard });
      submenu.push({ name: 'Entitlements', href: '/entitlements', icon: NavPackage });
    }

    return submenu;
  };

  // Check if user can access billing
  // Superadmins, org admins, and users with billing_view RBAC permission can access
  const canAccessBilling = () => {
    if (isSuperadmin) return true;
    if (isEffectiveOrgAdmin) return true;
    if (hasRbacPermission('billing_view')) return true;
    // If RBAC is not enabled, all users can see billing
    if (!rbacStatus?.rbac_enabled) return true;
    return false;
  };

  // Fetch RBAC status and SSO feature authorization on mount
  useEffect(() => {
    const fetchRbacStatus = async () => {
      if (!user) return;
      try {
        const response = await axios.get(
          `${getApiBase()}/rbac/status`,
          { headers: getAuthHeaders() }
        );
        setRbacStatus(response.data);
      } catch (err) {
        // RBAC may not be enabled or user may not have access
        console.log('RBAC status not available:', err.message);
      }
    };

    const fetchSsoFeatures = async () => {
      if (!user) return;
      try {
        const apiBase = getApiBase();
        const headers = getAuthHeaders();
        const [samlResponse, oidcResponse] = await Promise.all([
          axios.get(`${apiBase}/organizations/feature-check/saml`, { headers }).catch(() => ({ data: { enabled: false } })),
          axios.get(`${apiBase}/organizations/feature-check/oidc`, { headers }).catch(() => ({ data: { enabled: false } }))
        ]);
        setSsoFeaturesAuthorized({
          saml: samlResponse.data.enabled,
          oidc: oidcResponse.data.enabled
        });
      } catch (err) {
        console.log('SSO feature check not available:', err.message);
        setSsoFeaturesAuthorized({ saml: false, oidc: false });
      }
    };

    const fetchBookingFeature = async () => {
      if (!user) return;
      try {
        const apiBase = getApiBase();
        const headers = getAuthHeaders();
        const response = await axios.get(`${apiBase}/organizations/feature-check/booking`, { headers }).catch(() => ({ data: { enabled: false } }));
        setBookingEnabled(response.data.enabled || false);
      } catch {
        setBookingEnabled(false);
      }
    };

    fetchRbacStatus();
    fetchSsoFeatures();
    fetchBookingFeature();
  }, [user]);

  // For non-superadmins, eagerly fetch accessible orgs on mount so hasMultipleOrgs is set
  // For superadmins, fetch lazily when dropdown opens (they always see the switcher)
  useEffect(() => {
    if (!isSuperadmin && user && organizations.length === 0) {
      fetchOrganizations();
    }
  }, [isSuperadmin, user, organizations.length, fetchOrganizations]);

  useEffect(() => {
    if (isSuperadmin && orgDropdownOpen && organizations.length === 0) {
      fetchOrganizations();
    }
  }, [isSuperadmin, orgDropdownOpen, organizations.length, fetchOrganizations]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (orgDropdownOpen && !e.target.closest('.org-dropdown')) {
        setOrgDropdownOpen(false);
        setOrgSearchTerm('');
      }
      if (themeDropdownOpen && !e.target.closest('.theme-dropdown')) {
        setThemeDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [orgDropdownOpen, themeDropdownOpen]);

  // Focus search input when org dropdown opens
  useEffect(() => {
    if (orgDropdownOpen && orgSearchInputRef.current) {
      setTimeout(() => orgSearchInputRef.current?.focus(), 50);
    }
  }, [orgDropdownOpen]);

  // Filter organizations based on search term
  const filteredOrganizations = useMemo(() => {
    if (!orgSearchTerm) return organizations;
    const searchLower = orgSearchTerm.toLowerCase();
    return organizations.filter(org =>
      org.name?.toLowerCase().includes(searchLower) ||
      org.domain?.toLowerCase().includes(searchLower)
    );
  }, [organizations, orgSearchTerm]);

  // Get recent organizations
  const recentOrganizations = useMemo(() => {
    return recentOrgIds
      .slice(0, 5)
      .map(id => organizations.find(org => org.id === id))
      .filter(Boolean);
  }, [recentOrgIds, organizations]);

  // Handle organization switch with recent tracking and auth enforcement
  const handleOrgSwitch = async (org) => {
    if (org) {
      // Add to recent orgs
      const newRecent = [org.id, ...recentOrgIds.filter(id => id !== org.id)].slice(0, 10);
      setRecentOrgIds(newRecent);
      localStorage.setItem('recentOrganizations', JSON.stringify(newRecent));
    }
    const result = await switchOrganization(org);
    setOrgDropdownOpen(false);
    setOrgSearchTerm('');

    if (result?.requires_reauthentication) {
      // Target org requires re-authentication via a specific SSO provider
      alert(`${result.organization_name || 'This organization'} requires ${result.idp_name || 'SSO'} authentication. You will need to sign in with the required method.`);
      return;
    }

    if (result?.success !== false) {
      // Switching orgs invalidates org-scoped detail routes (e.g. /Trip/:id
      // points at a trip in the previous org, which 404s under the new acting
      // org). Land on the org-agnostic dashboard instead of reloading in place.
      if (window.location.pathname === '/') {
        window.location.reload();
      } else {
        window.location.assign('/');
      }
    }
  };

  // Handle setting a different org as primary
  const handleSetPrimary = async (e, org) => {
    e.stopPropagation(); // Don't trigger org switch
    if (!confirm(`Set "${org.name}" as your primary organization? This will change your default login org and email routing.`)) return;
    const result = await setPrimaryOrganization(org.id);
    if (result?.success) {
      // Changing primary org also invalidates org-scoped detail routes; land
      // on the dashboard rather than reloading a now-foreign trip/detail URL.
      if (window.location.pathname === '/') {
        window.location.reload();
      } else {
        window.location.assign('/');
      }
    } else {
      alert(result?.error || 'Failed to set primary organization');
    }
  };

  // Get the display name for current organization
  const currentOrgDisplay = actingOrganization
    ? actingOrganization.name || actingOrganization.id
    : user?.organization_id;

  const navigation = [
    { name: 'Home', href: '/', icon: NavHome },
    // Paradigm-aware setup and features guide for EA and speaker bureau orgs.
    ...(!isRestrictedUser ? [{ name: 'Getting Started', href: '/getting-started', icon: NavTarget }] : []),
    // Superadmin menu (only for superadmins)
    ...(user?.role === 'superadmin' ? [{
      name: 'Superadmin',
      icon: NavZap,
      dataTutorial: 'admin-nav-link',
      submenu: [
        // App Administration
        { name: 'App Administration', icon: NavWrench, isNested: true, submenu: [
          { name: 'Users / Permissions', href: '/admin-permissions', icon: NavShieldCheck },
        ]},
        // Organizations & Packages
        { name: 'Organizations & Packages', icon: NavBuilding, isNested: true, submenu: [
          { name: 'Organizations', href: '/organizations', icon: NavBuilding },
          { name: 'Packages', href: '/packages', icon: NavPackage },
          { name: 'Urbanride Account Linking', href: '/admin?tab=settings', icon: NavCar },
          { name: 'Deleted Organizations', href: '/deleted-organizations', icon: NavArchive },
        ]},
        // Content & Marketing
        { name: 'Content & Marketing', icon: NavRocket, isNested: true, submenu: [
          { name: 'Landing Page', href: '/landing', icon: NavRocket },
          { name: 'Landing Page (v2)', href: '/landing-v2', icon: NavRocket },
          { name: 'Landing Page (v3)', href: '/landing-v3', icon: NavRocket },
          { name: 'Publish Landing Page', href: '/landing-publish', icon: NavMegaphone },
          { name: 'Early Access Requests', href: '/early-access-requests', icon: NavFileText },
          { name: 'Legal Documents', href: '/legal-documents', icon: NavScrollText },
        ]},
        // User Management
        { name: 'User Management', icon: NavUsers, isNested: true, submenu: [
          { name: 'Users & Invites', href: '/admin', icon: NavUsers, dataTutorial: 'admin-users-link' },
          { name: 'RBAC Permissions', href: '/rbac', icon: NavShieldCheck },
          { name: 'Teams & Permissions', href: '/access-management', icon: NavKeyRound, dataTutorial: 'admin-access-link' },
          { name: 'Email Directory', href: '/access-management?tab=email-directory', icon: NavMail },
        ]},
        // Billing & Finance
        { name: 'Billing & Finance', icon: NavWallet, isNested: true, submenu: [
          { name: 'Admin Billing', href: '/admin-billing', icon: NavWallet },
          { name: 'Travel Transactions', href: '/admin-travel', icon: NavPlane },
          { name: 'Billing & Integrations', href: '/integrations', icon: NavCreditCard },
          { name: 'Billing Scheduler', href: '/admin/scheduler', icon: NavClock },
        ]},
        // Superadmin Dashboard (direct link, not nested)
        { name: 'Superadmin Dashboard', href: '/superadmin', icon: NavBarChart3 },
        // Parsing & Data
        { name: 'Parsing & Data', icon: NavTarget, isNested: true, submenu: [
          { name: 'Review Queue', href: '/parsing-review', icon: NavClipboardList },
          { name: 'QA Settings', href: '/parsing-qa-settings', icon: NavSettings },
          { name: 'Email Parsing Settings', href: '/email-parsing-settings', icon: NavMail },
          { name: 'AwardWallet Config', href: '/awardwallet-settings', icon: NavRefreshCw },
          { name: 'Parsing Comparisons', href: '/awardwallet-comparisons', icon: NavBarChart3 },
          { name: 'AI & Parsing Settings', href: '/superadmin-settings', icon: NavBot },
        ]},
        // Logs
        { name: 'Logs', icon: NavClipboardList, isNested: true, submenu: [
          { name: 'FlightAware Logs', href: '/flightaware-logs', icon: NavPlane },
          { name: 'Duffel API Logs', href: '/duffel-logs', icon: NavPlaneTakeoff },
          { name: 'Xero API Logs', href: '/xero-logs', icon: NavBarChart3 },
            { name: 'API Logs', href: '/logs', icon: NavFileText },
          { name: 'Google Maps Logs', href: '/google-places-logs', icon: NavMap },
          { name: 'Parsing Log', href: '/parsing-logs', icon: NavBot },
          { name: 'Audit Logs', href: '/audit-logs', icon: NavBarChart3 },
        ]},
        // Diagnostics
        { name: 'Diagnostics', icon: NavStethoscope, isNested: true, submenu: [
          { name: 'Email Visibility', href: '/diagnostics/email-visibility', icon: NavInbox },
          { name: 'FlightAware Reconciliation', href: '/diagnostics/flightaware-reconciliation', icon: NavHammer },
        ]},
        // Search & Book (standalone, not nested)
        { name: 'Search & Book', href: '/search', icon: NavSearch },
      ]
    }] : []),
    // Organization admins (non-superadmin) get admin menu
    // Uses isEffectiveOrgAdmin which respects RBAC when enabled
    ...(isEffectiveOrgAdmin && user?.role !== 'superadmin' ? [{
      name: 'Admin',
      icon: NavHammer,
      dataTutorial: 'admin-nav-link',
      submenu: [
        { name: 'Users & Invites', href: '/admin', icon: NavUsers, dataTutorial: 'admin-users-link' },
        { name: 'RBAC Permissions', href: '/rbac', icon: NavShieldCheck },
        { name: 'Teams & Permissions', href: '/access-management', icon: NavKeyRound, dataTutorial: 'admin-access-link' },
        { name: 'Email Directory', href: '/access-management?tab=email-directory', icon: NavMail },
        { name: 'Audit Logs', href: '/audit-logs', icon: NavClipboardList },
        // Only show Enterprise SSO if at least one SSO feature is authorized
        ...(ssoFeaturesAuthorized.saml || ssoFeaturesAuthorized.oidc ? [{ name: 'Enterprise SSO', href: '/sso-config', icon: NavKeyRound }] : []),
        // Billing and Entitlements for org admins
        { name: 'Billing', href: '/billing', icon: NavCreditCard },
        { name: 'Entitlements', href: '/entitlements', icon: NavPackage },
      ]
    }] : []),
    // Site Admin menu for non-superadmin users with admin permissions
    ...(!isSuperadmin && hasAnyAdminPermission ? [{
      name: 'Site Admin',
      icon: NavShieldCheck,
      submenu: buildSiteAdminSubmenu()
    }] : []),
    // RBAC Admin menu for users with specific RBAC permissions (but not org admins)
    ...(hasAnyRbacPermission ? [{
      name: 'Organization Admin',
      icon: NavKeyRound,
      submenu: buildRbacAdminSubmenu()
    }] : []),
    ...(!isRestrictedUser ? [{ name: 'Todos', href: '/todos', icon: NavCheckSquare, dataTutorial: 'todos-nav-link' }] : []),
    // Speakers navigate trips from their dashboard timeline/carousel only
    ...(!isSpeakerUser ? [{ name: 'Trips', href: '/trips', icon: NavMap, dataTutorial: 'trips-nav-link' }] : []),
    ...(!isRestrictedUser && (bookingEnabled || isSuperadmin) ? [
      { name: 'Search & Book', href: '/search', icon: NavSearch },
      { name: 'Bookings', href: '/bookings', icon: NavClipboardList },
    ] : []),
    ...(!isRestrictedUser ? [{ name: labels.executives, href: '/executives', icon: NavBriefcase, dataTutorial: 'executives-nav-link' }] : []),
    ...(!isRestrictedUser ? [{ name: 'Contacts', href: '/contacts', icon: NavUsers }] : []),
    ...(!isRestrictedUser ? [{
      name: 'Utilities',
      icon: NavWrench,
      submenu: [
        { name: 'Email Settings', href: '/email-settings', icon: NavMail, dataTutorial: 'email-settings-link' },
      ]
    }] : []),
    // Tutorial: single-track users start their tour directly; users with
    // multiple tracks (superadmins can review every track) get a picker
    ...(tutorialEnabledForOrg ? [(() => {
      const tutorialTracks = getAvailableTracks();
      if (tutorialTracks.length <= 1) {
        return { name: 'Tutorial', icon: NavGraduationCap, onClick: () => { startTutorial(); setSidebarOpen(false); } };
      }
      return {
        name: 'Tutorial',
        icon: NavGraduationCap,
        submenu: tutorialTracks.map((track) => ({
          name: TUTORIAL_TRACK_LABELS[track] || track,
          icon: NavGraduationCap,
          onClick: () => { startTutorial(track); setSidebarOpen(false); },
        })),
      };
    })()] : []),
    { name: 'Profile', href: '/profile', icon: NavSettings },
  ];

  const setSubmenuOpen = (menuName, open) => {
    setExpandedMenus(prev => ({ ...prev, [menuName]: open }));
  };

  return (
    <div className="h-screen flex flex-col bg-ui-page">
      {/* Top bar */}
      <header className="bg-ui-surface border-ui-divider shadow-sm border-b flex-shrink-0 safe-area-inset-top relative z-[60]">
        <div className="flex items-center justify-between h-16 sm:h-20 px-3 sm:px-4">
          <div className="flex items-center space-x-3 sm:space-x-4">
            <button
              type="button"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="lg:hidden p-2.5 sm:p-2 rounded-lg transition-colors touch-target text-ui-muted hover:text-ui-ink hover:bg-ui-hover"
              title={sidebarOpen ? "Close menu" : "Open menu"}
              aria-label={sidebarOpen ? "Close menu" : "Open menu"}
              aria-expanded={sidebarOpen}
            >
              {sidebarOpen ? (
                <X className="w-6 h-6 transition-transform duration-200" />
              ) : (
                <Menu className="w-6 h-6 transition-transform duration-200" />
              )}
            </button>
            <Link to="/">
              <img
                src="/veyage-logo-dark.svg"
                alt="Veyage"
                className="h-14 theme-modern-only"
              />
              <img
                src="/veyage-logo.svg"
                alt="Veyage"
                className="h-14 theme-simple-only"
              />
            </Link>
          </div>
          <div className="flex items-center space-x-2 sm:space-x-4">
            {/* Organization Selector for Superadmins */}
            {(isSuperadmin || hasMultipleOrgs) ? (
              <div className="relative org-dropdown" ref={orgDropdownRef}>
                <button
                  onClick={() => setOrgDropdownOpen(!orgDropdownOpen)}
                  className={`flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1.5 text-sm rounded-lg transition-colors ${
                    actingOrganization
                      ? 'bg-ui-amber-bg text-ui-amber-fg border border-ui-amber-line hover:bg-ui-amber-line'
                      : 'bg-ui-hover text-ui-ink-2 hover:bg-ui-line'
                  }`}
                  title="Switch organization"
                >
                  <Building2 className="w-4 h-4" />
                  <span className="hidden sm:inline max-w-[150px] truncate">{currentOrgDisplay}</span>
                  <ChevronDown className={`w-4 h-4 transition-transform ${orgDropdownOpen ? 'rotate-180' : ''}`} />
                </button>

                {orgDropdownOpen && (
                  <div className="fixed left-2 right-2 top-[4.5rem] w-auto max-w-[calc(100vw-1rem)] sm:absolute sm:left-auto sm:right-0 sm:top-auto sm:mt-2 sm:w-80 sm:max-w-none bg-ui-raised text-ui-ink rounded-lg shadow-ui-pop border border-ui-line z-50 max-h-[calc(100vh-6rem)] sm:max-h-[28rem] overflow-hidden flex flex-col">
                    {/* Search Input */}
                    <div className="p-2 border-b border-ui-divider">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ui-faint" />
                        <input
                          ref={orgSearchInputRef}
                          type="text"
                          value={orgSearchTerm}
                          onChange={(e) => setOrgSearchTerm(e.target.value)}
                          placeholder="Search organizations..."
                          className="w-full pl-9 pr-3 py-2 text-sm border border-ui-control rounded-md bg-ui-sunk text-ui-ink placeholder:text-ui-faint focus:outline-none focus:border-ui-primary focus:ring-1 focus:ring-ui-primary"
                        />
                      </div>
                      <div className="mt-1 text-xs text-ui-muted">
                        {filteredOrganizations.length} of {organizations.length} organizations
                      </div>
                    </div>

                    {/* Scrollable Content */}
                    <div className="overflow-y-auto flex-1">
                      {/* Option to return to own organization */}
                      {actingOrganization && !orgSearchTerm && (
                        <button
                          onClick={() => handleOrgSwitch(null)}
                          className="w-full px-3 py-2 text-left text-sm hover:bg-ui-blue-bg flex items-center gap-2 border-b border-ui-divider"
                        >
                          <span className="w-2 h-2 rounded-full bg-ui-blue-solid"></span>
                          <span className="font-medium text-ui-blue-fg">Return to my organization</span>
                        </button>
                      )}

                      {/* Recent Organizations */}
                      {recentOrganizations.length > 0 && !orgSearchTerm && (
                        <>
                          <div className="px-3 py-1.5 text-xs font-medium text-ui-muted uppercase bg-ui-sunk flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            Recent
                          </div>
                          {recentOrganizations.map(org => (
                            <button
                              key={`recent-${org.id}`}
                              onClick={() => handleOrgSwitch(org)}
                              className={`w-full px-3 py-2 text-left text-sm hover:bg-ui-hover flex items-center justify-between ${
                                actingOrganization?.id === org.id ? 'bg-ui-amber-bg' : ''
                              }`}
                            >
                              <div className="flex items-center gap-2 min-w-0">
                                <span className={`w-2 h-2 rounded-full flex-shrink-0 ${
                                  actingOrganization?.id === org.id ? 'bg-ui-amber-solid' : 'bg-ui-dash'
                                }`}></span>
                                <span className="truncate">{org.name || org.id}</span>
                              </div>
                              {org.domain && (
                                <span className="text-xs text-ui-faint ml-2 flex-shrink-0">{org.domain}</span>
                              )}
                            </button>
                          ))}
                        </>
                      )}

                      {/* All Organizations */}
                      {filteredOrganizations.length === 0 ? (
                        <div className="px-3 py-4 text-sm text-ui-muted text-center">
                          {orgSearchTerm ? 'No organizations match your search' : 'Loading organizations...'}
                        </div>
                      ) : (
                        <>
                          {recentOrganizations.length > 0 && !orgSearchTerm && (
                            <div className="px-3 py-1.5 text-xs font-medium text-ui-muted uppercase bg-ui-sunk">
                              All Organizations
                            </div>
                          )}
                          {filteredOrganizations.map(org => {
                            const isPrimary = org.is_own === true;
                            const isActive = actingOrganization?.id === org.id;
                            return (
                            <div
                              key={org.id}
                              className={`w-full px-3 py-2 text-left text-sm hover:bg-ui-hover flex items-center justify-between cursor-pointer ${
                                isActive ? 'bg-ui-amber-bg' : ''
                              }`}
                              onClick={() => handleOrgSwitch(org)}
                            >
                              <div className="flex items-center gap-2 min-w-0">
                                <span className={`w-2 h-2 rounded-full flex-shrink-0 ${
                                  isActive ? 'bg-ui-amber-solid' : 'bg-ui-dash'
                                }`}></span>
                                <span className="truncate">{org.name || org.id}</span>
                                {isPrimary && (
                                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-ui-blue-bg text-ui-blue-fg flex-shrink-0">Primary</span>
                                )}
                              </div>
                              <div className="flex items-center gap-1 flex-shrink-0">
                                {!isSuperadmin && !isPrimary && (
                                  <button
                                    onClick={(e) => handleSetPrimary(e, org)}
                                    className="text-[10px] px-1.5 py-0.5 rounded hover:bg-ui-blue-bg text-ui-faint hover:text-ui-link transition-colors"
                                    title="Set as primary organization"
                                  >
                                    Set primary
                                  </button>
                                )}
                              {org.domain && (
                                <span className="text-xs text-ui-faint ml-2">{org.domain}</span>
                              )}
                              </div>
                            </div>
                          );
                          })}
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <span className="text-sm hidden sm:inline text-ui-muted">
                <span className="hidden md:inline">Organization: </span>{user?.organization_name || user?.organization_id}
              </span>
            )}

            {/* Tutorial Launcher */}
            <TutorialLauncher />

            {/* Theme Selector */}
            <div className="relative theme-dropdown">
              <button
                onClick={() => setThemeDropdownOpen(!themeDropdownOpen)}
                className="flex items-center gap-2 px-3 py-1.5 text-sm rounded-lg transition-colors bg-ui-hover text-ui-ink-2 border border-ui-line hover:bg-ui-line"
                title="Change theme"
              >
                <Palette className="w-4 h-4" />
                <span className="hidden sm:inline">{THEME_CONFIG[theme]?.name || 'Theme'}</span>
                <ChevronDown className={`w-4 h-4 transition-transform ${themeDropdownOpen ? 'rotate-180' : ''}`} />
              </button>

              {themeDropdownOpen && (
                <div className="fixed left-2 right-2 top-[4.5rem] w-auto max-w-[calc(100vw-1rem)] sm:absolute sm:left-auto sm:right-0 sm:top-auto sm:mt-2 sm:w-48 sm:max-w-none rounded-lg shadow-ui-pop border z-[100] bg-ui-raised border-ui-line">
                  <div className="p-2 border-b border-ui-divider">
                    <p className="text-xs font-medium uppercase text-ui-muted">
                      Select Theme
                    </p>
                  </div>
                  {Object.entries(THEME_CONFIG).map(([key, config]) => (
                    <button
                      key={key}
                      onClick={() => {
                        setTheme(key);
                        setThemeDropdownOpen(false);
                      }}
                      className={`w-full px-3 py-2 text-left text-sm flex items-center justify-between hover:bg-ui-hover ${
                        theme === key ? 'bg-ui-blue-bg text-ui-blue-fg' : 'text-ui-ink-2'
                      }`}
                    >
                      <div>
                        <div className="font-medium">{config.name}</div>
                        <div className="text-xs text-ui-muted">
                          {config.description}
                        </div>
                      </div>
                      {theme === key && (
                        <span className="w-2 h-2 rounded-full bg-ui-blue-solid" />
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="flex items-center space-x-2 sm:space-x-3">
              <span className="text-sm hidden md:inline truncate max-w-[150px] text-ui-ink-2">
                {user?.email}
              </span>
              <button
                onClick={logout}
                className="inline-flex items-center px-3 py-2 sm:py-1.5 text-sm font-medium rounded-lg transition-colors duration-200 touch-target text-ui-muted hover:text-ui-ink hover:bg-ui-hover"
                title="Sign out of your account"
              >
                <svg className="w-5 h-5 sm:w-4 sm:h-4 sm:mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                <span className="hidden sm:inline">Sign Out</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="flex-1 flex min-h-0 relative">
        {/* Phones: the sidebar slides over the page; tapping outside closes it */}
        {sidebarOpen && (
          <div
            className="lg:hidden fixed inset-0 top-16 sm:top-20 z-50 transition-opacity duration-200 bg-ui-overlay/60"
            onClick={() => setSidebarOpen(false)}
            aria-hidden="true"
          />
        )}

        {/* App navigation: fixed beside the page on desktop, slide-out on phones */}
        <aside
          aria-label="App navigation"
          className={`fixed lg:static top-16 sm:top-20 bottom-0 left-0 z-50 w-72 lg:w-64 flex-none flex flex-col bg-ui-surface border-r border-ui-line shadow-ui-pop lg:shadow-none transition-transform duration-200 lg:translate-x-0 lg:visible ${sidebarOpen ? 'translate-x-0' : '-translate-x-full invisible'}`}
        >
          <div className="flex-1 overflow-y-auto scroll-smooth-touch">
            <AppSidebarNav
              navigation={navigation}
              expandedMenus={expandedMenus}
              toggleSubmenu={setSubmenuOpen}
              onNavigate={() => setSidebarOpen(false)}
            />
          </div>

          {/* User info footer */}
          <div className="p-4 border-t border-ui-divider bg-ui-hover/50">
            <div className="flex items-center justify-between">
              <div className="text-sm">
                <p className="font-medium truncate max-w-[200px] text-ui-ink">
                  {user?.email}
                </p>
                <p className="text-ui-muted">{getRoleLabel(user?.role)}</p>
              </div>
              <button
                onClick={() => {
                  setSidebarOpen(false);
                  logout();
                }}
                className="flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors duration-200 border text-ui-ink-2 bg-ui-hover hover:bg-ui-line border-ui-line"
                title="Sign out of your account"
              >
                <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                Sign Out
              </button>
            </div>
          </div>
        </aside>

        {/* Page content, beside the sidebar on desktop */}
      <main data-tutorial="dashboard-main" className="flex-1 overflow-x-hidden overflow-y-auto scroll-smooth-touch bg-ui-page">
        <div className={`w-full safe-area-inset-bottom ${fullBleed ? 'px-0 py-0' : 'px-3 sm:px-6 lg:px-8 py-4 sm:py-6'}`}>
          <Routes>
            <Route path="/" element={<ExecDashboardV2 />} />
            <Route path="/organizations" element={<OrganizationManagement />} />
            <Route path="/packages" element={<PackagesManagement />} />
            <Route path="/admin-permissions" element={<AdminPermissionsManagement />} />
            <Route path="/deleted-organizations" element={<OrganizationManagement showDeleted={true} />} />
            <Route path="/admin" element={<AdminDashboard />} />
            <Route path="/superadmin" element={<SuperadminDashboard />} />
            <Route path="/superadmin-settings" element={<SuperadminSettings />} />
            <Route path="/diagnostics/email-visibility" element={<EmailVisibilityDiagnostics />} />
            <Route path="/diagnostics/flightaware-reconciliation" element={<FlightAwareReconciliation />} />
            <Route path="/legal-documents" element={<LegalDocumentsManagement />} />
            <Route path="/integrations" element={<BillingIntegrations currentUser={user} />} />
            <Route path="/billing" element={
              canAccessBilling() ? (
                <BillingDashboard currentUser={user} />
              ) : (
                <div className="max-w-2xl mx-auto mt-8 p-6 bg-ui-surface rounded-lg shadow-ui-card">
                  <div className="text-center">
                    <div className="text-6xl mb-4">🔒</div>
                    <h2 className="text-xl font-semibold text-ui-ink mb-2">Access Denied</h2>
                    <p className="text-ui-ink-3">
                      You don't have permission to access the billing section.
                      Please contact your organization administrator for access.
                    </p>
                  </div>
                </div>
              )
            } />
            <Route path="/admin-billing" element={<AdminBillingDashboard currentUser={user} />} />
            <Route path="/admin-travel" element={<AdminTravelTransactions currentUser={user} />} />
            <Route path="/admin/scheduler" element={<SchedulerDashboard currentUser={user} />} />
            <Route path="/admin/scheduler/history" element={<SchedulerHistory currentUser={user} />} />
            <Route path="/entitlements" element={
              canAccessBilling() ? (
                <EntitlementsPage organizationId={user?.organization_id} currentUser={user} />
              ) : (
                <div className="max-w-2xl mx-auto mt-8 p-6 bg-ui-surface rounded-lg shadow-ui-card">
                  <div className="text-center">
                    <div className="text-6xl mb-4">🔒</div>
                    <h2 className="text-xl font-semibold text-ui-ink mb-2">Access Denied</h2>
                    <p className="text-ui-ink-3">
                      You don't have permission to access the entitlements section.
                      Please contact your organization administrator for access.
                    </p>
                  </div>
                </div>
              )
            } />
            <Route path="/admin/entitlements/:orgId" element={<AdminEntitlementsWrapper currentUser={user} />} />
            <Route path="/parsing-qa-settings" element={<ParsingQASettings />} />
            <Route path="/parsing-review" element={<ParsingQueueOverview />} />
            <Route path="/parsing-review/:reviewItemId" element={<ParsingReviewWorkstation />} />
            <Route path="/parsing-review/:reviewItemId/detached/:panelType" element={<DetachedPanelPage />} />
            <Route path="/parsing-review-legacy" element={<ParsingReviewQueue />} />
            <Route path="/parsing-review-legacy/:reviewItemId" element={<ParsingReviewQueue />} />
            <Route path="/email-parsing-settings" element={<EmailParsingSettings />} />
            <Route path="/awardwallet-settings" element={<AwardWalletSettings />} />
            <Route path="/awardwallet-comparisons" element={<AwardWalletComparisons />} />
            <Route path="/access-management" element={<AccessManagement />} />
            <Route path="/audit-logs" element={<AuditLogs />} />
            <Route path="/rbac" element={<RBACManagement />} />
            <Route path="/sso-config" element={<EnterpriseSSOConfiguration />} />
            <Route path="/organization" element={<OrganizationAdminDashboard />} />
            <Route path="/getting-started" element={isRestrictedUser ? <Navigate to="/" replace /> : <GettingStartedGuide />} />
            <Route path="/todos" element={isRestrictedUser ? <Navigate to="/" replace /> : <TodoManagement />} />
            <Route path="/trips" element={isSpeakerUser ? <Navigate to="/" replace /> : <TripManagement />} />
            <Route path="/Trip/:tripId" element={<TripDetailsWrapper />} />
            <Route path="/TripLegacy/:tripId" element={<TripDetailsLegacyWrapper />} />
            <Route path="/CreateTrip" element={isRestrictedUser ? <Navigate to="/" replace /> : <CreateTrip />} />
            <Route path="/executives" element={isRestrictedUser ? <Navigate to="/" replace /> : (isSpeakerBureau ? <SpeakerManagement /> : <ExecutiveManagement />)} />
            <Route path="/contacts" element={isRestrictedUser ? <Navigate to="/" replace /> : <ContactList />} />
            <Route path="/unfiled" element={isRestrictedUser ? <Navigate to="/" replace /> : <UnfiledReservations />} />
            <Route path="/email-settings" element={isRestrictedUser ? <Navigate to="/" replace /> : <EmailSettings />} />
            <Route path="/landing" element={<LandingPage />} />
            <Route path="/landing-v2" element={<LandingPageV2 />} />
            <Route path="/landing-v3" element={<LandingPageV3 />} />
            <Route path="/landing-publish" element={<LandingPagePublish />} />
            <Route path="/early-access-requests" element={<EarlyAccessRequests />} />
            <Route path="/upload" element={<DocumentUpload />} />
            <Route path="/search" element={<BookingSearch />} />
            <Route path="/bookings" element={<BookingManagement />} />
            <Route path="/chauffeur" element={<ChauffeuredTransferBooking />} />
            <Route path="/flightaware-logs" element={<FlightAwareLogs />} />
            <Route path="/parsing-logs" element={<ParsingLogs />} />
            <Route path="/logs" element={<Logs />} />
            <Route path="/google-places-logs" element={<GooglePlacesLogs />} />
            <Route path="/duffel-logs" element={<DuffelLogs />} />
            <Route path="/xero-logs" element={<XeroLogs />} />
            <Route path="/profile" element={<Profile />} />
          </Routes>
        </div>
      </main>
      </div>
    </div>
  );
};

export default Dashboard;
